import { searchScore } from "@/lib/search-text";

/**
 * Ordenación de agencias para el panel de administración y el buscador de la
 * cabecera. Sin dependencias de servidor: se usa en ambos lados.
 */

export const CAMPOS_ORDEN = [
  "nombre",
  "region",
  "provincia",
  "anuncios",
  "solicitudes",
  "miembros",
  "creada",
] as const;
export type CampoOrden = (typeof CAMPOS_ORDEN)[number];

/** `campo` = ascendente, `-campo` = descendente (se guarda así en la URL). */
export const ORDENES_AGENCIAS = CAMPOS_ORDEN.flatMap((c) => [c, `-${c}`] as const);
export type OrdenAgencias = (typeof ORDENES_AGENCIAS)[number];
export const ORDEN_AGENCIAS_DEFECTO: OrdenAgencias = "-creada";

const CAMPOS_TEXTO = new Set<CampoOrden>(["nombre", "region", "provincia"]);

type AgenciaOrdenable = {
  nombre: string;
  region?: string | null;
  provincia_nombre?: string | null;
  anuncios_count?: number | null;
  solicitudes_count?: number | null;
  miembros_count?: number | null;
  created_at?: string | null;
};

const collator = new Intl.Collator("es", { sensitivity: "base", numeric: true });

function valor(r: AgenciaOrdenable, campo: CampoOrden): string | number | null {
  switch (campo) {
    case "nombre":
      return r.nombre?.trim() || null;
    case "region":
      return r.region?.trim() || null;
    case "provincia":
      return r.provincia_nombre?.trim() || null;
    case "anuncios":
      return r.anuncios_count ?? 0;
    case "solicitudes":
      return r.solicitudes_count ?? 0;
    case "miembros":
      return r.miembros_count ?? 0;
    case "creada":
      return r.created_at ? Date.parse(r.created_at) : null;
  }
}

export function parseOrden(orden: OrdenAgencias): { campo: CampoOrden; desc: boolean } {
  const desc = orden.startsWith("-");
  return { campo: (desc ? orden.slice(1) : orden) as CampoOrden, desc };
}

/**
 * Siguiente orden al pulsar una columna: si ya se ordena por ella se invierte;
 * si no, los textos empiezan A→Z y los números de mayor a menor.
 */
export function siguienteOrden(actual: OrdenAgencias, campo: CampoOrden): OrdenAgencias {
  const a = parseOrden(actual);
  if (a.campo === campo) return (a.desc ? campo : `-${campo}`) as OrdenAgencias;
  return (CAMPOS_TEXTO.has(campo) ? campo : `-${campo}`) as OrdenAgencias;
}

/** Ordena sin mutar. Los vacíos van siempre al final; a igualdad, por nombre. */
export function ordenarAgencias<T extends AgenciaOrdenable>(rows: T[], orden: OrdenAgencias): T[] {
  const { campo, desc } = parseOrden(orden);
  const signo = desc ? -1 : 1;
  return [...rows].sort((x, y) => {
    const a = valor(x, campo);
    const b = valor(y, campo);
    if (a == null && b != null) return 1;
    if (b == null && a != null) return -1;
    let c = 0;
    if (a != null && b != null) {
      c =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : collator.compare(String(a), String(b));
    }
    if (c !== 0) return c * signo;
    return collator.compare(x.nombre ?? "", y.nombre ?? "");
  });
}

/**
 * Buscador de la cabecera: primero las agencias cuyo nombre coincide con la
 * búsqueda (empieza por ella, luego palabra que empieza, luego contiene) y
 * después las que solo coinciden por email, web o teléfono; a igualdad, las
 * que tienen más anuncios.
 */
export function ordenarResultadosAgencias<T extends AgenciaOrdenable>(rows: T[], q: string): T[] {
  const puntuar = (r: T) => {
    const s = searchScore(r.nombre ?? "", q);
    return s < 0 ? 3 : s;
  };
  return [...rows].sort(
    (x, y) =>
      puntuar(x) - puntuar(y) ||
      (y.anuncios_count ?? 0) - (x.anuncios_count ?? 0) ||
      collator.compare(x.nombre ?? "", y.nombre ?? ""),
  );
}
